import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type EdgeChange,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, Paper, Stack, Typography, Button, CircularProgress, Alert } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useTranslation } from 'react-i18next';
import {
  useArbeitspakete,
  useApDependencies,
  useAddApDependency,
  useRemoveApDependency,
} from '../../../api/hooks/useArbeitspakete.js';
import { useNotifications } from '../../../context/NotificationContext.js';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog.js';
import { ApFlowNode, type ApFlowNodeData, type ApStatus } from './ApFlowNode.js';
import { layoutNodes } from '../lib/dagreLayout.js';
import { wouldCreateCycle } from '../lib/wouldCreateCycle.js';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';

interface Arbeitspaket {
  id: string;
  nr: string;
  titel: string | null;
  status: ApStatus;
  progress: number;
}

interface ApDependency {
  from_ap_id: string;
  to_ap_id: string;
}

const nodeTypes: NodeTypes = { apFlowNode: ApFlowNode };

const EDGE_STYLE = { stroke: '#1D8296', strokeWidth: 2 };

function buildNodes(items: Arbeitspaket[]): Node[] {
  return items.map((ap) => ({
    id: ap.id,
    type: 'apFlowNode',
    position: { x: 0, y: 0 },
    data: {
      nr: ap.nr,
      titel: ap.titel,
      status: ap.status,
      progress: ap.progress,
    } satisfies ApFlowNodeData,
  }));
}

function buildEdges(deps: ApDependency[]): Edge[] {
  return deps.map((d) => ({
    id: `${d.from_ap_id}->${d.to_ap_id}`,
    source: d.from_ap_id,
    target: d.to_ap_id,
    animated: true,
    style: EDGE_STYLE,
  }));
}

interface InnerProps {
  projectId: string;
}

function ApDependencyEditorInner({ projectId }: InnerProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const apsQuery = useArbeitspakete(projectId);
  const depsQuery = useApDependencies(projectId);
  const addMut = useAddApDependency(projectId);
  const removeMut = useRemoveApDependency(projectId);

  const items = useMemo<Arbeitspaket[]>(
    () => ((apsQuery.data as unknown as { data?: Arbeitspaket[] } | undefined)?.data ?? []),
    [apsQuery.data],
  );
  const deps = useMemo<ApDependency[]>(
    () => ((depsQuery.data as unknown as { data?: ApDependency[] } | undefined)?.data ?? []),
    [depsQuery.data],
  );

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [edgeToDelete, setEdgeToDelete] = useState<Edge | null>(null);
  const [hasLayouted, setHasLayouted] = useState(false);

  // Initial-Sync (Server -> lokaler State) inkl. einmaligem Auto-Layout
  useEffect(() => {
    if (apsQuery.isLoading || depsQuery.isLoading) return;
    const baseNodes = buildNodes(items);
    const baseEdges = buildEdges(deps);
    if (!hasLayouted && baseNodes.length > 0) {
      setNodes(layoutNodes(baseNodes, baseEdges));
      setEdges(baseEdges);
      setHasLayouted(true);
    } else if (!hasLayouted) {
      setNodes(baseNodes);
      setEdges(baseEdges);
    } else {
      // Folge-Updates: Position behalten, Daten + Edges aktualisieren
      setNodes((prev) => {
        const prevById = new Map(prev.map((n) => [n.id, n]));
        return baseNodes.map((n) => {
          const existing = prevById.get(n.id);
          return existing ? { ...n, position: existing.position } : n;
        });
      });
      setEdges(baseEdges);
    }
  }, [items, deps, apsQuery.isLoading, depsQuery.isLoading, hasLayouted]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onConnect = useCallback(
    async (connection: Connection) => {
      const { source, target } = connection;
      if (!source || !target) return;
      if (source === target) {
        notify(t('apDependencies.selfLoop', 'Selbstbezug ist nicht erlaubt.'), 'warning');
        return;
      }
      const exists = edges.some((e) => e.source === source && e.target === target);
      if (exists) {
        notify(t('apDependencies.duplicate', 'Diese Abhängigkeit existiert bereits.'), 'info');
        return;
      }
      if (wouldCreateCycle(edges, source, target)) {
        notify(t('apDependencies.cycle', 'Zyklus erkannt — Abhängigkeit nicht möglich.'), 'error');
        return;
      }

      // Optimistisch hinzufügen
      setEdges((eds) =>
        addEdge({ ...connection, animated: true, style: EDGE_STYLE }, eds),
      );
      try {
        await addMut.mutateAsync({ from_ap_id: source, to_ap_id: target });
        notify(t('apDependencies.added', 'Abhängigkeit hinzugefügt.'), 'success');
      } catch (err) {
        // Rollback bei Fehler
        setEdges((eds) => eds.filter((e) => !(e.source === source && e.target === target)));
        notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
      }
    },
    [edges, addMut, notify, t, tc],
  );

  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setEdgeToDelete(edge);
  }, []);

  const handleDeleteEdge = useCallback(async () => {
    if (!edgeToDelete) return;
    const { source, target } = edgeToDelete;
    try {
      await removeMut.mutateAsync({ fromId: source, toId: target });
      setEdges((eds) => eds.filter((e) => e.id !== edgeToDelete.id));
      notify(t('apDependencies.removed', 'Abhängigkeit entfernt.'), 'success');
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    } finally {
      setEdgeToDelete(null);
    }
  }, [edgeToDelete, removeMut, notify, t, tc]);

  const handleAutoLayout = useCallback(() => {
    setNodes((nds) => layoutNodes(nds, edges));
  }, [edges]);

  if (apsQuery.isLoading || depsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (apsQuery.isError || depsQuery.isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {tc('errors.generic', 'Fehler beim Laden.')}
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('apDependencies.empty', 'Noch keine Arbeitspakete vorhanden. Lege zuerst APs an.')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t(
            'apDependencies.hint',
            'Verbinde APs per Drag von der rechten Handle zur linken Handle des nächsten APs. Klick auf eine Kante löscht sie.',
          )}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={handleAutoLayout}
        >
          {t('apDependencies.autoLayout', 'Auto-Layout')}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ height: 600, borderRadius: 1, overflow: 'hidden' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </Paper>

      <ConfirmDialog
        open={!!edgeToDelete}
        title={t('apDependencies.deleteTitle', 'Abhängigkeit löschen?')}
        message={t(
          'apDependencies.deleteConfirm',
          'Die Abhängigkeit zwischen diesen Arbeitspaketen wird entfernt.',
        )}
        confirmColor="error"
        onConfirm={handleDeleteEdge}
        onCancel={() => setEdgeToDelete(null)}
      />
    </Box>
  );
}

export function ApDependencyEditor({ projectId }: { projectId: string }) {
  return (
    <ReactFlowProvider>
      <ApDependencyEditorInner projectId={projectId} />
    </ReactFlowProvider>
  );
}
